import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, Layers } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import { SectionCard } from "../../../../components/design-system/admin/SectionCard";
import { EmptyState } from "../../../../components/design-system/admin/EmptyState";
import { useProductFamiliesApi } from "../../../../common/hooks/use-product-families-api.hook";
import { ProductFamily } from "../../../../common/models/product-family.model";
import { Vendor } from "../../our-vendors/models/vendor.model";
import { useResolveTranslation } from "../../../../common/hooks/use-resolve-translation.hook";

interface VendorFamiliesProps {
    vendor: Vendor;
    setVendor: (vendor: Vendor) => void;
}

export const VendorFamilies = ({ vendor, setVendor }: VendorFamiliesProps) => {
    const { t } = useTranslation();
    const { rt } = useResolveTranslation();
    const familiesApi = useProductFamiliesApi();
    const [families, setFamilies] = useState<ProductFamily[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadFamilies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadFamilies = async () => {
        try {
            setIsLoading(true);
            const allFamilies = await familiesApi.findAllFamilies({ activeOnly: true });
            setFamilies(allFamilies);
        } catch (error) {
            console.error('Failed to load product families:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleFamily = (familyId: string) => {
        const currentFamilies = vendor.enabledFamilies || [];
        const isEnabled = currentFamilies.includes(familyId);

        // Disabling: just drop the family. Leave areas untouched so the
        // superadmin's manual selections are never silently wiped.
        if (isEnabled) {
            setVendor({
                ...vendor,
                enabledFamilies: currentFamilies.filter(id => id !== familyId)
            });
            return;
        }

        // Enabling: smart-default the vendor's areas from this family's
        // defaultApplicationTypes (e.g. surface-products -> floor, walls).
        // interactive_floor is retired, so it's never derived. Furniture has
        // no application types and therefore contributes no surface area.
        // Areas stay fully editable via the chips on the Profile tab.
        const family = families.find(f => f._id === familyId);
        const derived = (family?.visualizationConfig?.defaultApplicationTypes || [])
            .map(a => a.toLowerCase())
            .filter(a => a !== 'interactive_floor');

        const existingNames = new Set((vendor.areas || []).map(a => a.name.toLowerCase()));
        const addedAreas = derived
            .filter(a => !existingNames.has(a))
            .map(name => ({ name }));

        setVendor({
            ...vendor,
            enabledFamilies: [...currentFamilies, familyId],
            areas: [...(vendor.areas || []), ...addedAreas] as Vendor['areas']
        });
    };

    const isFamilyEnabled = (familyId: string) => {
        return (vendor.enabledFamilies || []).includes(familyId);
    };

    return (
        <SectionCard
            title={t("ADMIN_VENDOREDIT.CATALOG.FAMILIES_TITLE")}
            description={t("ADMIN_VENDOREDIT.CATALOG.FAMILIES_DESCRIPTION")}
            action={
                !isLoading && families.length > 0 ? (
                    <span className="text-xs text-muted-foreground tabular-nums">
                        {t("ADMIN_VENDOREDIT.CATALOG.FAMILIES_COUNT", {
                            enabled: (vendor.enabledFamilies || []).length,
                            total: families.length,
                        })}
                    </span>
                ) : undefined
            }
            testId="vendor-families-card"
        >
            {isLoading ? (
                <div className="flex flex-col gap-2">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
                </div>
            ) : families.length === 0 ? (
                <EmptyState
                    icon={<Layers />}
                    title={t("ADMIN_VENDOREDIT.CATALOG.FAMILIES_EMPTY_TITLE")}
                    description={t("ADMIN_VENDOREDIT.CATALOG.FAMILIES_EMPTY_BODY")}
                />
            ) : (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {families.map((family) => {
                        const isEnabled = isFamilyEnabled(family._id);
                        return (
                            <button
                                key={family._id}
                                type="button"
                                role="checkbox"
                                aria-checked={isEnabled}
                                onClick={() => handleToggleFamily(family._id)}
                                data-testid={`vendor-family-${family._id}`}
                                className={cn(
                                    "flex cursor-pointer items-start rounded-lg border p-4 text-start transition-colors",
                                    // Selected = ink outline, never a tint.
                                    isEnabled
                                        ? "border-foreground ring-1 ring-inset ring-foreground"
                                        : "hover:bg-accent/50",
                                )}
                            >
                                {/* A drawn checkbox: the whole row is the control (a real one would nest a button in a button). */}
                                <span
                                    aria-hidden
                                    className={cn(
                                        "me-3 mt-0.5 grid size-4 shrink-0 place-items-center rounded-[4px] border",
                                        isEnabled ? "border-foreground bg-foreground text-background" : "border-input",
                                    )}
                                >
                                    {isEnabled && <Check className="size-3" />}
                                </span>
                                <span className="flex-1">
                                    <span className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium">{rt(family.name)}</span>
                                        {family.isDefault && (
                                            <Badge variant="outline" className="text-muted-foreground">
                                                {t("ADMIN_VENDOREDIT.CATALOG.FAMILY_DEFAULT")}
                                            </Badge>
                                        )}
                                        {family.isSystemFamily && (
                                            <Badge variant="outline" className="text-muted-foreground">
                                                {t("ADMIN_VENDOREDIT.CATALOG.FAMILY_SYSTEM")}
                                            </Badge>
                                        )}
                                    </span>
                                    {family.description && (
                                        <span className="mt-1 block text-xs text-muted-foreground">{rt(family.description)}</span>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </SectionCard>
    );
};
